import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home as HomeIcon, QrCode, Settings as SettingsIcon, PiggyBank, Shirt } from 'lucide-react';
import MyQR from '../components/MyQR';
import Benefits from '../components/Benefits';
import Settings from '../components/Settings';
import MiAhorro from '../components/MiAhorro';
import MuestrarioPrecios from '../components/MuestrarioPrecios';
import { authService } from '../services/auth';
import Footer from '../components/Footer';
import { VD, GRAD, SOMBRA } from '../lib/brand';

const Home = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  /* Se puede aterrizar directo en una pestaña con ?tab=qr — lo usa el alta con
     beneficio, que manda al usuario a su código recién desbloqueado. */
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return ['benefits', 'qr', 'savings', 'showcase', 'settings'].includes(t) ? t : 'benefits';
  });
  const [user, setUser] = useState(null);

  // La autenticación la garantiza ProtectedRoute. La suscripción ya NO se exige
  // aquí: desde el flujo freemium, /home está abierta a cuentas gratuitas y el
  // muro vive dentro, en cada tarjeta.
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (currentUser.role === 'admin') {
      navigate('/admin');
      return;
    }
    setUser(currentUser);
  }, [navigate]);

  if (!user) {
    return (
      <div className="min-h-screen bg-vd-niebla flex items-center justify-center">
        <div className="text-vd-pizarra text-base">Cargando...</div>
      </div>
    );
  }

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const mainTabs = [
    { id: 'benefits',      icon: HomeIcon   },
    { id: 'qr',            icon: QrCode     },
    // Módulo 6: el ahorro del cliente. Va junto al QR a propósito — es donde
    // aterriza después de canjear, y es la pantalla que empuja a pagar.
    { id: 'savings',       icon: PiggyBank  },
    /*
      Muestrario de precios reales. Va en **pestaña propia** por pedido de Iván
      (12-ago-2026): estaba embutido arriba del catálogo y su formato pide una
      pantalla, no una sección.

      Se agrega al final para no mover de sitio las pestañas que la gente ya
      usa —sobre todo el QR, que se abre con prisa en la caja—. Cambiarlo de
      posición es mover este renglón.
    */
    { id: 'showcase',      icon: Shirt      },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'benefits':      return <Benefits user={user} />;
      case 'qr':            return <MyQR />;
      case 'savings':       return <MiAhorro />;
      case 'showcase':      return <MuestrarioPrecios />;
      case 'settings':      return <Settings user={user} onLogout={handleLogout} />;
      default:              return null;
    }
  };

  return (
    <div className="min-h-screen bg-vd-niebla flex flex-col">


      {/* Content Area with tab transition */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="flex-1 overflow-y-auto pb-24"
        >
          {renderContent()}
          <Footer className="mt-8 mb-4" />
        </motion.div>
      </AnimatePresence>

      {/* Bottom Navigation — pill + circle */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        padding: '0 20px max(28px, env(safe-area-inset-bottom, 28px))',
        display: 'flex', alignItems: 'center', gap: 10,
        pointerEvents: 'none',
      }}>
        {/* Pill: Home · QR · Avisos — ocupa todo el espacio disponible */}
        <div style={{
          flex: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'space-evenly',
          background: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          borderRadius: 9999,
          padding: '6px 12px',
          boxShadow: SOMBRA.md,
          pointerEvents: 'all',
        }}>
          {mainTabs.map(({ id, icon: Icon }) => {
            const isActive = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                style={{
                  flex: '1 1 0', minWidth: 0, maxWidth: 56, height: 52, borderRadius: 9999,
                  background: isActive ? GRAD.vivo : 'transparent',
                  border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background .18s ease',
                }}>
                <Icon size={20} color={isActive ? '#fff' : VD.pizarra} />
              </button>
            );
          })}
        </div>

        {/* Circle: Mi Cuenta */}
        <button
          onClick={() => setActiveTab('settings')}
          style={{
            flexShrink: 0,
            width: 64, height: 64, borderRadius: '50%',
            background: activeTab === 'settings' ? GRAD.vivo : 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: SOMBRA.md,
            pointerEvents: 'all',
            transition: 'background .18s ease',
          }}>
          <SettingsIcon size={20} color={activeTab === 'settings' ? '#fff' : VD.pizarra} />
        </button>
      </div>
    </div>
  );
};

export default Home;
